/**
 * Lightweight cross-subdomain session indicator cookie.
 *
 * localStorage is origin-scoped, so a session on corporate.valuegiftvouchers.com
 * is invisible to valuegiftvouchers.com. We use a tiny cookie scoped to
 * .valuegiftvouchers.com so the main-domain navbar can detect any active portal
 * session and show Account/Logout instead of Log In/Get Started.
 *
 * This does NOT share auth tokens — the real Supabase session stays in
 * localStorage per-subdomain. This is display-only.
 */

import type { PortalId } from "@/lib/portal";
import { getPortalUrl } from "@/lib/portal";

const COOKIE_NAME = "vgv_portal";
const MAX_AGE = 60 * 60 * 24 * 400; // 400 days — longer than any Supabase refresh token

function cookieDomain(): string {
  const h = window.location.hostname;
  if (h === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return "";
  const parts = h.split(".");
  return parts.length >= 2 ? `.${parts.slice(-2).join(".")}` : "";
}

export function setSessionIndicator(portalId: PortalId): void {
  const domain = cookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : "";
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${portalId}; path=/${domainAttr}; max-age=${MAX_AGE}; SameSite=Lax${secure}`;
}

export function clearSessionIndicator(): void {
  const domain = cookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : "";
  document.cookie = `${COOKIE_NAME}=; path=/${domainAttr}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/** Broadcasts an explicit logout to all portals. Other portals see this and evict themselves. */
export function setSignedOutIndicator(): void {
  const domain = cookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : "";
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=signed_out; path=/${domainAttr}; max-age=3600; SameSite=Lax${secure}`;
}

export function getSessionIndicator(): PortalId | "signed_out" | null {
  const match = document.cookie.match(/(?:^|;\s*)vgv_portal=([^;]+)/);
  const val = match?.[1];
  if (!val) return null;
  if (val === "signed_out") return "signed_out";
  return val as PortalId;
}

/**
 * Returns the active portal ID, or null if signed out or no session exists.
 * Use this for display / navigation. Use getSessionIndicator() only in
 * ProtectedRoute where the "signed_out" sentinel is needed for eviction.
 */
export function getActivePortal(): PortalId | null {
  const indicator = getSessionIndicator();
  if (!indicator || indicator === "signed_out") return null;
  return indicator;
}

const PORTAL_DASHBOARD: Record<PortalId, string> = {
  consumer:  "/app/consumer/vouchers",
  merchant:  "/app/merchant/dashboard",
  agent:     "/app/agent/dashboard",
  corporate: "/app/corporate/dashboard",
  admin:     "/app/admin/dashboard",
};

/** Account URL for the main-domain navbar when the real session is on a subdomain. */
export function getIndicatorAccountUrl(portalId: PortalId): string {
  return getPortalUrl(portalId, PORTAL_DASHBOARD[portalId] ?? "/");
}
